import { Component, EventEmitter, Input, Output, Inject, Optional, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { AutoInvoiceMethodItem, BrRegItem, CustomerBonusDealItem, CustomerDiscountGroupItem, CustomerEditGet, CustomerEditParams, CustomerGroup2Item, CustomerGroupItem, CustomerService, EhfGroupItem, InvoiceIntervalItem, UserItem } from '@app/services/customer.service';
import { AccountItem, PostPlaceItem, TypeaheadService } from '@app/services/typeahead.service';
import { PostplaceGet } from '../postplace/postplace.component';
import { BusinesscoItem, BusinessItem, CoopItem, CountryItem, DeliveryMethodItem, DeliveryTermItem, LogisticService, StockplaceItem } from '@app/services/logistic.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ILanguageInfo, LanguageService } from '@app/core/services/language.service';
import { EconomyService, FactoringItem, InvoiceMethodsItem, PaymentTypeItem, PriceListItem, TaxItem, ValutaItem } from '@app/services/economy.service';
import { catchError, debounceTime, EMPTY, from, Subject, switchMap } from 'rxjs';
import { ToastService } from '@app/core/services/toast.service';
import { StateDialogContext, TT_STATE_DIALOG, TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';

@GridModalComponent('customeredit')
@Component({
    selector: 'tt-customer-edit',
    templateUrl: './customer-edit.component.html',
    styleUrls: ['./customer-edit.component.css'],
})
export class CustomerEditComponent implements OnChanges, OnInit {
    @Input()
    public params?: CustomerEditParams;

    public invalid: Partial<{ [key in keyof CustomerEditGet]: { invalid: boolean; message: string; message_modal: string } }> = {};

    public get?: CustomerEditGet;

    public countries: CountryItem[] = [];

    public companies: BusinesscoItem[] = [];

    public languages: ILanguageInfo[] = [];

    public portalUsers: Item[] = [];

    public groupingMethods: EhfGroupItem[] = [];

    public autoInvoiceMethods: AutoInvoiceMethodItem[] = [];

    public stockplaces: StockplaceItem[] = [];

    public users: UserItem[] = [];

    public invoiceIntervals: InvoiceIntervalItem[] = [];

    public deliveryMethods: DeliveryMethodItem[] = [];

    public deliveryTerms: DeliveryTermItem[] = [];

    public customerGroups: CustomerGroupItem[] = [];

    public customerGroups2: CustomerGroup2Item[] = [];

    public customerDiscountGroups: CustomerDiscountGroupItem[] = [];

    public sellers: Item[] = [];

    public businesses: BusinessItem[] = [];

    public coops: CoopItem[] = [];

    public valutas: ValutaItem[] = [];

    public pricelist: PriceListItem[] = [];

    public paymentTypes: PaymentTypeItem[] = [];

    public taxes: TaxItem[] = [];

    public invoiceMethods: InvoiceMethodsItem[] = [];

    public factorings: FactoringItem[] = [];

    public customerBonusDeals: CustomerBonusDealItem[] = [];

    private autoSave$ = new Subject<void>();

    private addContactsAfterNewCustomer = false;

    public saving = false;

    @Output()
    public submit = new EventEmitter<CustomerEditGet & { addContactsAfterNewCustomer: boolean }>();

    @Output()
    public getLoaded = new EventEmitter<CustomerEditGet>();

    constructor(
        private customer: CustomerService,
        private typeahead: TypeaheadService,
        private logistic: LogisticService,
        private economy: EconomyService,
        private language: LanguageService,
        private modal: ModalService,
        private toast: ToastService,
        @Inject(TT_STATE_DIALOG) @Optional() stateDialog?: StateDialogContext,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.initFromDialog(stateDialog);

        this.autoSave$
            .pipe(
                debounceTime(750),
                switchMap(() => from(this.save()).pipe(catchError(() => EMPTY))),
            )
            .subscribe({
                next: (value) => {
                    if (!!value && value.error === false && value.errors.length === 0) {
                        this.toast.addToast({ id: `purchhead.save.success`, type: 'success', title: 'saved!', translateTitle: true, message: 'changes_have_been_saved', translateMessage: true });
                    } else {
                        this.toast.addToast({ id: `purchhead.save.error`, type: 'danger', title: '', translateTitle: true });
                    }
                },
            });
    }

    public async manualSave() {
        if (!this.get || !this.params) return;

        try {
            this.saving = true;

            this.resetInvalids();

            if (await this.doesCustomerWithCustNoExists()) {
                this.modal.show({ type: 'warning', title: 'could_not_save', translateTitle: true, message: 'customer_with_cust_no_already_exists', translateMessage: true });
                return;
            }

            const save = await this.customer.saveCustomerEdit(this.get);

            if (save.error === false) {
                if (!!this.navigator && this.get.autosave !== '1') {
                    if (this.addContactsAfterNewCustomer && this.params.cust_no === '0') {
                        this.navigator.sequence([
                            { to: 'customeredit', parms: `${save.cust_no}`, options: { location: 'replace' } },
                            { to: 'customerpersons', parms: { cust_no: `${save.cust_no}`, new_customer: true } },
                        ]);
                    } else {
                        this.navigator.back(undefined, { ...this.get, cust_no: `${save.cust_no}` });
                    }
                }

                this.submit.emit({ ...this.get, cust_no: `${save.cust_no}`, addContactsAfterNewCustomer: this.addContactsAfterNewCustomer && this.params.cust_no === '0' });
            } else {
                for (const error of save.errors) {
                    this.invalid[error.column] = { invalid: true, message: error.message, message_modal: error.message_modal };
                }

                const longMessage = save.errors
                    .filter((error) => !!error.message_modal)
                    .map((error) => `- ${error.message_modal}`)
                    .join('\n');

                if (longMessage.trim() > '') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: longMessage });
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private async save() {
        if (!this.get || !this.params || this.saving) return;

        try {
            this.saving = true;

            this.resetInvalids();

            if (await this.doesCustomerWithCustNoExists()) {
                this.modal.show({ type: 'warning', title: 'could_not_save', translateTitle: true, message: 'customer_with_cust_no_already_exists', translateMessage: true });
                return;
            }

            const save = await this.customer.saveCustomerEdit(this.get);

            for (const error of save.errors) {
                this.invalid[error.column] = { invalid: true, message: error.message, message_modal: error.message_modal };
            }

            return save;
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }

        throw Error('could not save');
    }

    private async doesCustomerWithCustNoExists() {
        if (!this.get || !this.params) throw Error('get must be loaded before checking');

        let existingCustomer = false;

        if (this.get.set_cust_no === '1' && this.params.cust_no === '0') {
            existingCustomer = !!(await this.customer.getCustomersMenu({ cust_no: this.get.cust_no }));
        }

        return existingCustomer;
    }

    public openUrlInNewWindow() {
        if (!!this.get?.cust_www) {
            if (this.get.cust_www.startsWith('http')) {
                window.open(this.get.cust_www);
            } else {
                window.open(`https://${this.get.cust_www}`);
            }
        }
    }

    public onCustNoChanged(event: `${number}` | number) {
        if (!this.get) return;

        if (this.get.syncronize_acc_cust_no_with_cust_no === '1') {
            this.get.acc_cust_no = ('' + event) as `${number}`;
        }

        this.onCommonChange();
    }

    public brRegSearch = () => {
        return this.customer.brregSearch(this.get?.brreg_find_customer ?? '');
    };

    public onBrRegSelected(event: BrRegItem) {
        if (!this.get) return;

        this.get.cust_name = event.navn;
        this.get.cust_name_sort = event.navn;
        this.get.org_no = event.organisasjonsnummer;
        this.get.country_id = event?.forretningsadresse?.landkode?.toLowerCase() ?? this.get.country_id;
        this.get.cust_www = event?.hjemmeside ?? '';

        if (event?.postadresse) {
            this.get.cust_adr_mail = event.postadresse.adresse?.[0] ?? '';
            this.get.postplace_id = event.postadresse.postnummer;
            this.get.postplace_name = event.postadresse.poststed;
            this.get.postplace_search_mail = `${event.postadresse.postnummer} ${event.postadresse.poststed}`;
        } else if (event?.forretningsadresse) {
            this.get.cust_adr_mail = event.forretningsadresse.adresse?.[0] ?? '';
            this.get.postplace_id = event.forretningsadresse.postnummer;
            this.get.postplace_name = event.forretningsadresse.poststed;
            this.get.postplace_search_mail = `${event.forretningsadresse.postnummer} ${event.forretningsadresse.poststed}`;
        }

        if (event?.forretningsadresse) {
            this.get.cust_adr_street = event.forretningsadresse.adresse?.[0] ?? '';
            this.get.postplace_id_street = event.forretningsadresse.postnummer;
            this.get.postplace_name_street = event.forretningsadresse.poststed;
            this.get.postplace_search_street = `${event.forretningsadresse.postnummer} ${event.forretningsadresse.poststed}`;
        }

        this.onCommonChange();
    }

    public async openPostplaceDialog() {
        const result: PostplaceGet | undefined = await this.modal.openStateDialog({ state: 'postplace', params: '0' });

        if (!!result) {
            this.onPostplaceSelected({ ...result, postplace_id_and_name: `${result.postplace_id} ${result.postplace_name}` });
        }
    }

    public postplaceSearch = () => {
        return this.typeahead.searchPostplaces({ searchtext: this.get?.postplace_search_street ?? '' });
    };

    public onPostplaceSelected(event?: Pick<PostPlaceItem, 'postplace_id' | 'country_id' | 'postplace_id_and_name'>) {
        if (!this.get) return;

        if (!!event) {
            this.get.postplace_id_street = event.postplace_id;
            this.get.postplace_search_street = event.postplace_id_and_name;
            this.get.country_id = event.country_id;

            if (!this.get.postplace_id) {
                this.get.postplace_id = event.postplace_id;
                this.get.postplace_search_mail = event.postplace_id_and_name;
            }
        } else {
            this.get.postplace_id_street = '';
            this.get.postplace_search_street = '';
        }

        this.onCommonChange();
    }

    public async openPostplaceMailDialog() {
        const result: PostplaceGet | undefined = await this.modal.openStateDialog({ state: 'postplace', params: '0' });

        if (!!result) {
            this.onPostplaceMailSelected({ ...result, postplace_id_and_name: `${result.postplace_id} ${result.postplace_name}` });
        }
    }

    public postplaceMailSearch = () => {
        return this.typeahead.searchPostplaces({ searchtext: this.get?.postplace_search_mail ?? '' });
    };

    public onPostplaceMailSelected(event?: any) {
        if (!this.get) return;

        if (!!event) {
            this.get.postplace_id = event.postplace_id;
            this.get.postplace_search_mail = event.postplace_id_and_name;
            this.get.country_id = event.country_id;

            if (!this.get.postplace_id_street) {
                this.get.postplace_id_street = event.postplace_id;
                this.get.postplace_search_street = event.postplace_id_and_name;
            }
        } else {
            this.get.postplace_id = '';
            this.get.postplace_search_mail = '';
        }

        this.onCommonChange();
    }

    public deliveryCustomerSearch = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.delivery_cust_name ?? '', custtype: this.get?.delivery_customer_search_type ?? 'C' });
    };

    public onDeliveryCustomerSelected(event?: Item) {
        if (!this.get) return;

        this.get.delivery_cust_no = !!event ? '' + event.item_id : '0';
        this.get.delivery_cust_name = event?.item_name ?? '';

        this.onCommonChange();
    }

    public invoiceCustomerSearch = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.invoice_cust_name ?? '', custtype: 'C' });
    };

    public onInvoiceCustomerSelected(event?: Item) {
        if (!this.get) return;

        this.get.invoice_cust_no = !!event ? '' + event.item_id : '0';
        this.get.invoice_cust_name = event?.item_name ?? '';

        this.onCommonChange();
    }

    public searchAccounts = async () => {
        if (!this.get) return [];

        return this.typeahead.lookUpSearchAccounts({ searchtext: this.get.account_name_purchase, businessco_no: this.get.businessco_no });
    };

    public onAccountPurchaseSelected(event?: AccountItem) {
        if (!this.get) return;

        this.get.account_no_purchase = event?.item_id ?? '';
        this.get.account_name_purchase = event?.item_name ?? '';
    }

    public onBusinessCoChanged(_: string) {
        this.loadPortalUsers();
        this.loadSellers();
        this.onCommonChange();
    }

    public async onIsCustomerChanged() {
        if (this.get?.is_customer === '1') {
            const { acc_cust_no } = await this.customer.assignNewAccCustNo(this.get);
            this.get.acc_cust_no = acc_cust_no;
        }
        this.onCommonChange();
    }

    public async onIsSupplierChanged() {
        if (this.get?.is_supplyer === '1' && this.get.acc_supp_no <= '0') {
            const { nextnumber } = await this.customer.getNextCounter({ counter_id: 'acc_supp_no', cust_no: this.get.cust_no });
            this.get.acc_supp_no = nextnumber;
        }
        this.onCommonChange();
    }

    public async onCustomerGroupChanged(_: string) {
        if (!this.get) return;

        const { cust_delm_no, cust_delt_no, cust_invoice_valuta_id, cust_order_valuta_id, cust_paym_id, pricelist_id, pricelist_id2, purchase_pricelist_id, purchase_pricelist_id2, tax_keyno } = await this.customer.getCustomerGroupChangedData({ custgrp_id: this.get.custgrp_id });

        this.get.cust_delm_no = cust_delm_no;
        this.get.cust_delt_no = cust_delt_no;
        this.get.cust_invoice_valuta_id = cust_invoice_valuta_id;
        this.get.cust_order_valuta_id = cust_order_valuta_id;
        this.get.cust_paym_id = cust_paym_id;
        this.get.pricelist_id = pricelist_id;
        this.get.pricelist_id2 = pricelist_id2;
        this.get.purchase_pricelist_id = purchase_pricelist_id;
        this.get.purchase_pricelist_id2 = purchase_pricelist_id2;
        this.get.tax_keyno = tax_keyno;

        this.onCommonChange();
    }

    public onCommonChange() {
        if (!this.get || !this.params) return;

        if (this.params.cust_no > '0') {
            if (this.get.autosave === '1') {
                this.autoSave$.next();
            }
        }
    }

    public reset() {
        this.resetInvalids();
    }

    private resetInvalids() {
        for (const key in this.invalid) {
            this.invalid[key as keyof CustomerEditGet] = { invalid: false, message: '', message_modal: '' };
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.customer.getCustomerEdit(this.params);
            this.getLoaded.emit(this.get);
        }
    }

    private async loadCountries() {
        this.countries = await this.logistic.getCountries();
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadPortalUsers() {
        if (!!this.get) {
            this.portalUsers = await this.logistic.getPurchaserPortalUsers({ businessco_no: this.get.businessco_no });
        }
    }

    private async loadLanguages() {
        this.languages = await this.language.List();
    }

    private async loadGroupingMethods() {
        this.groupingMethods = await this.customer.getEhfGroupingMethods();
    }

    private async loadDeliveryMethods() {
        this.deliveryMethods = await this.logistic.getDeliveryMethods();
    }

    private async loadDeliveryTerms() {
        this.deliveryTerms = await this.logistic.getDeliveryTerms();
    }

    private async loadCustomerGroups() {
        this.customerGroups = await this.customer.getCustomerGroups();
    }

    private async loadCustomerGroups2() {
        this.customerGroups2 = await this.customer.getCustomerGroups2();
    }

    private async loadCustomerDiscountGroups() {
        this.customerDiscountGroups = await this.customer.getCustomerDiscountGroups({ add_from_custgrp: '1' });
    }

    private async loadSellers() {
        if (!!this.get) {
            this.sellers = await this.economy.getSellers({ businessco_no: this.get.businessco_no });
        }
    }

    private async loadBusinesses() {
        this.businesses = await this.logistic.getBusinesses();
    }

    private async loadCoops() {
        this.coops = await this.logistic.getCoops();
    }

    private async loadValutas() {
        this.valutas = await this.economy.getValutas();
    }

    private async loadPricelist() {
        this.pricelist = await this.economy.getPriceLists();
    }

    private async loadPayments() {
        this.paymentTypes = await this.economy.getPaymentTypes();
    }

    private async loadTaxes() {
        this.taxes = await this.economy.getTaxes();
    }

    private async loadInvoiceMethods() {
        this.invoiceMethods = await this.economy.getInvoiceMethods();
    }

    private async loadFactorings() {
        this.factorings = await this.economy.getFactorings();
    }

    private async loadCustomerBonusDeals() {
        this.customerBonusDeals = await this.customer.getCustomerBonusDeals();
    }

    private async componentInit() {
        const { addContactsAfterNewCustomer, autoinvoicemethods, invoiceintervals, stockplaces, users } = await this.customer.customerEditInit('init');

        this.addContactsAfterNewCustomer = addContactsAfterNewCustomer;
        this.autoInvoiceMethods = autoinvoicemethods;
        this.stockplaces = stockplaces;
        this.users = users;
        this.invoiceIntervals = invoiceintervals;
    }

    private async init() {
        this.componentInit();
        this.loadCountries();
        this.loadCompanies();
        this.loadLanguages();
        this.loadGroupingMethods();
        this.loadDeliveryMethods();
        this.loadDeliveryTerms();
        this.loadCustomerGroups();
        this.loadCustomerGroups2();
        this.loadCustomerDiscountGroups();
        this.loadBusinesses();
        this.loadCoops();
        this.loadValutas();
        this.loadPricelist();
        this.loadPayments();
        this.loadTaxes();
        this.loadInvoiceMethods();
        this.loadFactorings();
        this.loadCustomerBonusDeals();
        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.webpage_heading ?? '');
        this.loadPortalUsers();
        this.loadSellers();
    }

    private async initFromDialog(stateDialog?: StateDialogContext) {
        if (!!stateDialog?.params && 'cust_no' in stateDialog.params) {
            this.submit.subscribe({ next: (value: any) => stateDialog.close(value) });
            this.params = stateDialog.params as { cust_no: `${number}` };
            stateDialog.setSize({ height: '80vh', width: '90vh' });
            await this.init();
            stateDialog.setHeading(this.get?.webpage_heading ?? '');
        }
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ height: '80vh', width: '90vh' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
}
